import * as PopoverPrimitive from "@radix-ui/react-popover";
import { cva } from "class-variance-authority";
import { AnimatePresence, m } from "framer-motion";
import { Check, ChevronDown, X } from "lucide-react";
import { type ReactNode, useId, useMemo, useRef, useState } from "react";
import { cn } from "../cn";
import { springSoft, useMotionTransition } from "../motion";
import { buttonVariantStyles } from "./Button";
import { useSheetPortalContainer } from "./Sheet";

/**
 * The trigger is a button-shaped control but not a Button: Button centers
 * content, squishes on click and sizes its icons, none of which fits a
 * left-aligned, truncated label with an overlay icon cluster. Only the
 * color tokens are shared, via `buttonVariantStyles`.
 */
const comboboxTriggerVariants = cva(
  "inline-flex items-center rounded-xl pl-3 text-sm text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/15 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "border border-input bg-surface shadow-sm hover:bg-muted/50 focus-visible:border-primary aria-[invalid=true]:border-destructive",
        ghost: buttonVariantStyles.ghost,
      },
      size: {
        sm: "h-9",
        md: "h-10",
      },
      fluid: {
        true: "w-full",
        false: "min-w-40",
      },
    },
    defaultVariants: { variant: "default", size: "md", fluid: false },
  },
);

export interface ComboboxOption {
  readonly value: string;
  readonly label: string;
  readonly hint?: string;
}

interface ComboboxBaseProps {
  readonly value: string | undefined;
  readonly options: readonly ComboboxOption[];
  /**
   * Applied to the trigger button, so a `Field`'s `<label htmlFor>` points at
   * the element the user actually operates.
   */
  readonly id?: string;
  readonly "aria-label"?: string;
  readonly "aria-labelledby"?: string;
  readonly "aria-describedby"?: string;
  readonly "aria-invalid"?: boolean;
  /**
   * Not applied to the trigger button directly: `aria-required` is not in the
   * button role's supported-property set, so AT would ignore it there. It is
   * applied to whichever element carries `role="combobox"` — the trigger when
   * `searchable={false}`, otherwise the popover's search input.
   */
  readonly "aria-required"?: boolean;
  readonly placeholder?: string;
  readonly searchPlaceholder?: string;
  readonly emptyText?: string;
  readonly loading?: boolean;
  readonly size?: "sm" | "md";
  /**
   * `default` renders the full input chrome (border, surface, shadow).
   * `ghost` drops it — like Button's ghost variant — for inline controls
   * that should read as plain text until hovered.
   */
  readonly variant?: "default" | "ghost";
  readonly className?: string;
  /**
   * Called with the raw search-input text on every keystroke, and with `""`
   * when the combobox clears the text itself (on selecting an option or
   * closing the popover), so a server-driven list resets with the input.
   * Optional —
   * only needed when `options` is server-driven (e.g. a debounced dossier
   * search) rather than a fixed local list. When provided, the component
   * still applies its own client-side `label.includes(query)` filter on top
   * of whatever `options` comes back, so the caller doesn't need to guard
   * against a stale response containing extra rows.
   */
  readonly onQueryChange?: (query: string) => void;
  /**
   * When `true`, the combobox fills its container's width (`w-full`) so it
   * lines up with sibling `Input`/`Textarea`/`Select` controls in a form
   * column or grid. Defaults to `false`: the trigger shrinks to its
   * intrinsic content width (with a sensible `min-w-40` floor), which is
   * what compact toolbar/filter rows want.
   */
  readonly fluid?: boolean;
  /**
   * Overrides the trigger's text while leaving the option list untouched.
   * Lets a caller keep options searchable by a long label ("Nederland (+31)")
   * while the trigger shows only the compact part that still matters once
   * chosen ("+31"). Defaults to the selected option's `label`.
   */
  readonly triggerLabel?: ReactNode;
  /**
   * Renders an option's label in the dropdown, so callers can decorate every
   * row — e.g. prefixing a color dot — without mapping the options array.
   * Only the label is slotted: the row chrome, `hint` and selected check stay
   * Combobox-owned, and the plain `label` still drives search filtering and
   * the trigger text.
   */
  readonly renderOptionLabel?: (option: ComboboxOption) => ReactNode;
  readonly disabled?: boolean;
  /**
   * Decorative leading icon inside the trigger (e.g. a filter glyph). Always
   * `aria-hidden` — the trigger's `aria-label` already names the control.
   */
  readonly icon?: ReactNode;
  /**
   * Extra classes for the trigger button itself. `className` lands on the
   * wrapper, which can't reach the button — a caller embedding the combobox
   * in a larger control group needs this to strip its border/shadow or
   * override the default min-width.
   */
  readonly triggerClassName?: string;
  /**
   * Whether the popover includes a search field. Defaults to `true`.
   *
   * Set `false` for short fixed lists (a handful of enum values), where a
   * search box over three options is chrome that costs a keystroke and earns
   * nothing. The trigger then carries the combobox semantics and keyboard
   * handling itself, so arrow keys, Enter and Escape behave identically —
   * only the typing-to-filter affordance goes away.
   */
  readonly searchable?: boolean;
  /**
   * Shrinks the trigger to just its `icon` (e.g. an avatar) for dense rows
   * where the selected label would not fit. The label stays in the trigger as
   * screen-reader text and the chevron and clear button are left out; the
   * popover keeps its own minimum width, so searching works as usual.
   * Clearing is still possible by re-selecting the chosen option.
   */
  readonly iconOnly?: boolean;
}

interface ClearableComboboxProps {
  /**
   * Whether the trigger offers a clear (×) button once a value is selected,
   * and whether re-clicking the selected option deselects it. Defaults to
   * `true`. Set `false` when the field always needs *some* value, so clearing
   * it would only produce an invalid state — the type then guarantees
   * `onValueChange` is never called with `undefined`.
   */
  readonly clearable?: true;
  readonly onValueChange: (value: string | undefined) => void;
}

interface NonClearableComboboxProps {
  readonly clearable: false;
  readonly onValueChange: (value: string) => void;
}

export type ComboboxProps = ComboboxBaseProps &
  (ClearableComboboxProps | NonClearableComboboxProps);

/**
 * Single-select combobox (WAI-ARIA combobox pattern). Options are selected via
 * `aria-activedescendant` rather than roving tabindex, so arrow keys never move
 * DOM focus away from the element that owns the combobox role — the search
 * input when `searchable` (the default), otherwise the trigger itself.
 */
export function Combobox({
  value,
  onValueChange,
  options,
  id,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  "aria-required": ariaRequired,
  placeholder = "Selecteer...",
  searchPlaceholder = "Zoeken...",
  emptyText = "Geen resultaten",
  loading = false,
  size = "md",
  variant = "default",
  className,
  onQueryChange,
  fluid = false,
  triggerLabel,
  renderOptionLabel,
  clearable = true,
  disabled = false,
  icon,
  triggerClassName,
  searchable = true,
  iconOnly = false,
}: ComboboxProps) {
  const popoverTransition = useMotionTransition(springSoft);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef(new Map<number, HTMLButtonElement>());
  const listboxId = useId();
  // When rendered inside a Sheet, portal into its panel so the dropdown lives
  // within the Dialog's scroll-lock boundary and stays wheel-scrollable.
  const sheetContainer = useSheetPortalContainer();

  const listedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );
  // A server-driven list replaces its options as the search changes, and resets
  // with the search once an option is picked, so the picked one is not
  // necessarily in it any more. The last one seen is kept for as long as it is
  // the selection, so the trigger keeps its label and its clear button.
  const [rememberedOption, setRememberedOption] = useState<ComboboxOption>();
  if (
    listedOption &&
    (listedOption.value !== rememberedOption?.value ||
      listedOption.label !== rememberedOption.label)
  ) {
    setRememberedOption(listedOption);
  }
  const selectedOption =
    listedOption ??
    (value !== undefined && rememberedOption?.value === value
      ? rememberedOption
      : undefined);

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return options;
    }
    return options.filter((option) =>
      option.label.toLowerCase().includes(normalizedQuery),
    );
  }, [options, query]);

  const optionId = (index: number) => `${listboxId}-option-${index}`;

  const scrollOptionIntoView = (index: number) => {
    optionRefs.current.get(index)?.scrollIntoView({ block: "nearest" });
  };

  // Only reachable when `clearable` is true: the clear button is hidden
  // otherwise and `selectOption` guards on it. TS can't correlate the two
  // destructured union members, so the cast expresses what the guard knows.
  const emitClear = (): void => {
    (onValueChange as (value: string | undefined) => void)(undefined);
  };

  const resetQuery = () => {
    if (query === "") return;
    setQuery("");
    onQueryChange?.("");
  };

  const selectOption = (option: ComboboxOption) => {
    if (option.value === value) {
      // Not clearable: re-clicking the selection keeps it.
      if (clearable) emitClear();
    } else {
      onValueChange(option.value);
      // The list may be replaced in the same breath (see `rememberedOption`).
      setRememberedOption(option);
    }
    setOpen(false);
    resetQuery();
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      resetQuery();
    } else {
      setActiveIndex(0);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (!open) {
      if (
        event.key === "ArrowDown" ||
        event.key === "ArrowUp" ||
        event.key === "Enter"
      ) {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }

    switch (event.key) {
      case "ArrowDown": {
        event.preventDefault();
        setActiveIndex((current) => {
          const next =
            filteredOptions.length === 0
              ? 0
              : (current + 1) % filteredOptions.length;
          scrollOptionIntoView(next);
          return next;
        });
        break;
      }
      case "ArrowUp": {
        event.preventDefault();
        setActiveIndex((current) => {
          const next =
            filteredOptions.length === 0
              ? 0
              : (current - 1 + filteredOptions.length) % filteredOptions.length;
          scrollOptionIntoView(next);
          return next;
        });
        break;
      }
      case "Home": {
        event.preventDefault();
        setActiveIndex(0);
        scrollOptionIntoView(0);
        break;
      }
      case "End": {
        event.preventDefault();
        const last = Math.max(0, filteredOptions.length - 1);
        setActiveIndex(last);
        scrollOptionIntoView(last);
        break;
      }
      case "Enter": {
        event.preventDefault();
        const option = filteredOptions[activeIndex];
        if (option) {
          selectOption(option);
        }
        break;
      }
      case "Escape": {
        event.preventDefault();
        setOpen(false);
        break;
      }
      default:
        break;
    }
  };

  const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    emitClear();
  };

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <div
        className={cn(
          "relative",
          fluid ? "flex w-full" : "inline-flex",
          className,
        )}
      >
        <PopoverPrimitive.Trigger asChild>
          <button
            type="button"
            id={id}
            disabled={loading || disabled}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            aria-describedby={ariaDescribedBy}
            aria-invalid={ariaInvalid}
            aria-haspopup="listbox"
            aria-expanded={open}
            // Without a search field the trigger owns the combobox semantics,
            // so arrow keys, Enter and Escape keep working while focus stays
            // put. With one, the input below carries them instead.
            // aria-required rides along with this conditional spread on
            // purpose: the button role does not support aria-required (AT
            // ignores it there), but the combobox role does, so it only goes
            // where the role that supports it goes. Do not "tidy" it up to the
            // plain attributes above — that silently breaks it.
            //
            // aria-invalid and aria-describedby stay on this button too, even
            // in searchable mode where focus moves to the input below: this
            // is the element aria-[invalid=true]:border-destructive paints,
            // so the closed trigger still shows the error state. The input
            // carries its own copies below for the AT-facing semantics, since
            // it is what actually owns role="combobox" once open.
            {...(searchable
              ? {}
              : {
                  role: "combobox" as const,
                  "aria-controls": listboxId,
                  "aria-required": ariaRequired,
                  "aria-activedescendant":
                    open && filteredOptions.length > 0
                      ? optionId(activeIndex)
                      : undefined,
                  onKeyDown: handleKeyDown,
                })}
            className={cn(
              comboboxTriggerVariants({ variant, size, fluid }),
              iconOnly
                ? "min-w-0 justify-center px-1.5"
                : selectedOption && clearable && !disabled && !loading
                  ? "pr-14"
                  : "pr-8",
              triggerClassName,
            )}
          >
            {icon && (
              <span
                aria-hidden="true"
                className={cn(
                  "inline-flex shrink-0 text-muted-foreground",
                  !iconOnly && "mr-2",
                )}
              >
                {icon}
              </span>
            )}
            <span
              className={cn(
                iconOnly ? "sr-only" : "truncate",
                !selectedOption && "text-muted-foreground",
              )}
            >
              {triggerLabel ?? selectedOption?.label ?? placeholder}
            </span>
          </button>
        </PopoverPrimitive.Trigger>
        {/*
          Grouped in a single flex cluster (instead of independently
          positioned icons) so the clear button and chevron can never
          overlap regardless of icon size. The cluster itself ignores
          pointer events so clicks over the chevron fall through to the
          trigger button beneath it; only the clear button re-enables
          pointer events for itself.
        */}
        {!iconOnly && (
          <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center gap-1">
            {selectedOption && clearable && !disabled && !loading && (
              <button
                type="button"
                aria-label="Wissen"
                onClick={handleClear}
                className="pointer-events-auto grid size-5 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X className="size-3.5" aria-hidden="true" />
              </button>
            )}
            <ChevronDown
              className="size-4 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
          </div>
        )}
      </div>
      <AnimatePresence>
        {open && (
          <PopoverPrimitive.Portal
            forceMount
            container={sheetContainer ?? undefined}
          >
            <PopoverPrimitive.Content
              asChild
              forceMount
              align="start"
              sideOffset={4}
              onOpenAutoFocus={(event) => {
                event.preventDefault();
                // Without a search field there is nothing to focus inside the
                // popover; focus stays on the trigger, which owns the keyboard
                // handling in that mode.
                searchInputRef.current?.focus();
              }}
            >
              <m.div
                initial={{ opacity: 0, scale: 0.96, y: 4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97, y: 2 }}
                transition={popoverTransition}
                className={cn(
                  "z-50 w-[var(--radix-popover-trigger-width)] overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-lg focus:outline-none",
                  // The 14rem floor exists to give the search field room to
                  // type in; without one it just makes the popover wider than
                  // the trigger it belongs to.
                  searchable && "min-w-56",
                )}
              >
                {searchable && (
                  <div className="border-b border-border/60 p-1.5">
                    <input
                      ref={searchInputRef}
                      type="text"
                      role="combobox"
                      aria-label={ariaLabel}
                      aria-labelledby={ariaLabelledBy}
                      aria-expanded={open}
                      aria-controls={listboxId}
                      aria-autocomplete="list"
                      aria-required={ariaRequired}
                      aria-invalid={ariaInvalid}
                      aria-describedby={ariaDescribedBy}
                      aria-activedescendant={
                        filteredOptions.length > 0
                          ? optionId(activeIndex)
                          : undefined
                      }
                      value={query}
                      onChange={(event) => {
                        setQuery(event.target.value);
                        setActiveIndex(0);
                        onQueryChange?.(event.target.value);
                      }}
                      onKeyDown={handleKeyDown}
                      placeholder={searchPlaceholder}
                      className="w-full rounded-lg bg-transparent px-2 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
                    />
                  </div>
                )}
                <div
                  id={listboxId}
                  role="listbox"
                  aria-label={ariaLabel}
                  aria-labelledby={ariaLabelledBy}
                  className="max-h-64 overflow-y-auto p-1"
                >
                  {filteredOptions.length === 0 && (
                    <p className="px-2 py-4 text-center text-sm text-muted-foreground">
                      {emptyText}
                    </p>
                  )}
                  {filteredOptions.map((option, index) => {
                    const isSelected = option.value === value;
                    const isActive = index === activeIndex;
                    return (
                      <button
                        key={option.value}
                        ref={(node) => {
                          if (node) {
                            optionRefs.current.set(index, node);
                          } else {
                            optionRefs.current.delete(index);
                          }
                        }}
                        type="button"
                        id={optionId(index)}
                        role="option"
                        tabIndex={-1}
                        aria-selected={isSelected}
                        onMouseEnter={() => setActiveIndex(index)}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => selectOption(option)}
                        className={cn(
                          "flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors",
                          isActive && "bg-primary-soft text-primary",
                          !isActive && "text-foreground hover:bg-muted/50",
                        )}
                      >
                        <span className="truncate">
                          {renderOptionLabel
                            ? renderOptionLabel(option)
                            : option.label}
                          {option.hint && (
                            <span className="ml-1.5 text-muted-foreground">
                              {option.hint}
                            </span>
                          )}
                        </span>
                        {isSelected && (
                          <Check
                            className="size-4 shrink-0"
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </m.div>
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        )}
      </AnimatePresence>
    </PopoverPrimitive.Root>
  );
}
